import { z } from 'zod';

import type { Role } from './enums';

/**
 * Password rules for the dormant username/password sign-in (`AUTH_PROVIDERS=password`).
 *
 * One rule for the HR form, the change-password dialog, the API and the CLI. Following
 * NIST SP 800-63B: a length floor and a blocklist are ENFORCED; character-mix "rules" are
 * only shown as guidance under the field, never required - they push people towards
 * `Password1!`, which is on every leak list anyway.
 */

/** The top of every leak list - refused outright, compared on the whole password. */
const COMMON = new Set([
  'password', 'password1', 'password12', 'password123', 'password@123', 'passw0rd', 'p@ssw0rd', 'p@ssword',
  '12345678', '123456789', '1234567890', '87654321', '11111111', '00000000', 'qwerty123', 'qwertyuiop',
  '1q2w3e4r', '1qaz2wsx', 'iloveyou', 'admin123', 'admin@123', 'welcome1', 'welcome123', 'welcome@123',
  'letmein1', 'abc12345', 'abcd1234', 'changeme', 'sunshine', 'football', 'baseball', 'monkey123', 'india123',
]);

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 200;

/** True for a password every attacker tries first: on the list, one repeated character, or a plain run. */
export function isCommonPassword(pw: string): boolean {
  const lower = pw.toLowerCase();
  return (
    COMMON.has(lower) ||
    /^(.)\1+$/.test(pw) ||
    'abcdefghijklmnopqrstuvwxyz'.includes(lower) ||
    '01234567890'.includes(lower)
  );
}

/** The guidance checklist under the field, in display order. Never enforced. */
export const PASSWORD_CHECKS = [
  { label: 'At least 12 characters', test: (pw: string) => pw.length >= 12 },
  { label: 'One uppercase letter', test: (pw: string) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw: string) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw: string) => /\d/.test(pw) },
] as const;

export const PASSWORD_LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'] as const;

export interface PasswordStrength {
  /** 0 too weak (cannot be saved) ... 4 strong. */
  score: 0 | 1 | 2 | 3 | 4;
  label: (typeof PASSWORD_LABELS)[number];
  checks: { label: string; met: boolean }[];
  /** Why it is "too weak", when it is; empty otherwise. */
  problem: string;
}

/**
 * Live strength for the meter. Too short or common = too weak (the API refuses those);
 * otherwise one step per checklist item met, plus one for 16+ characters so a long
 * passphrase is never "weak".
 * ponytail: heuristic, not zxcvbn - swap in a dictionary scorer if people game it.
 */
export function passwordStrength(pw: string): PasswordStrength {
  const checks = PASSWORD_CHECKS.map((c) => ({ label: c.label, met: c.test(pw) }));
  const problem =
    pw.length < PASSWORD_MIN_LENGTH
      ? `Use at least ${PASSWORD_MIN_LENGTH} characters`
      : pw.length > PASSWORD_MAX_LENGTH
        ? `Use at most ${PASSWORD_MAX_LENGTH} characters`
        : isCommonPassword(pw)
          ? 'Too common - pick something less guessable'
          : '';
  const met = checks.filter((c) => c.met).length + Number(pw.length >= 16);
  const score = (problem ? 0 : Math.max(1, Math.min(4, met))) as PasswordStrength['score'];
  return { score, label: PASSWORD_LABELS[score], checks, problem };
}

/** A new password as the API accepts it: 8-200 characters and not a common one. */
export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `At least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `At most ${PASSWORD_MAX_LENGTH} characters`)
  .refine((pw) => !isCommonPassword(pw), 'Too common - pick something less guessable');

/** Changing your own password: the current one proves it is you. */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password').max(PASSWORD_MAX_LENGTH),
  newPassword: newPasswordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Whether `actor` may set `target`'s password from the HR employee form.
 *
 * Setting somebody's password is being able to sign in as them, so an HR user setting a
 * fellow HR user's (or a super admin's) would be a way up the ladder. Only SUPER_ADMIN may
 * set an HR or SUPER_ADMIN account's; HR may set anyone else's. Your own is exempt - it is
 * how a Google-only admin gives themselves a password when the change dialog (which needs
 * the current one) has nothing to check against.
 */
export function canSetPasswordFor(
  actor: { id: string; role: Role },
  target: { id: string; role: Role },
): boolean {
  if (actor.id === target.id) return true;
  if (actor.role === 'SUPER_ADMIN') return true;
  return actor.role === 'HR' && target.role !== 'HR' && target.role !== 'SUPER_ADMIN';
}
